import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, X, Plus, HeartPulse, Building2, ArrowRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FormInput from '../../components/FormInput';
import SelectInput from '../../components/SelectInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as usersApi from '../../api/users.api';
import { errorMessage } from '../../api/client';

const GENDERS = ['MALE', 'FEMALE', 'OTHER'];

// ChipInput — tag/chip field with defensive UX (client feedback 2026-08-18):
//   • Enter or comma auto-converts typed text into a chip (no need to
//     tap "+ Add").
//   • Blur auto-adds too, so switching fields doesn't drop the value.
//   • Pasted strings with commas split into multiple chips at once.
//   • Parent can call `flushRef.current()` before submit to catch any
//     text still sitting in the input — returns the up-to-date array so
//     the caller doesn't have to wait for React state to settle.
//   • "+ Add" button turns primary (solid) as soon as there's text.
function ChipInput({ label, values, onChange, placeholder, helper, flushRef }) {
  const [v, setV] = useState('');

  // Split on commas so a pasted "penicillin, aspirin" becomes two chips.
  function addMany(raw, currentList) {
    const parts = String(raw || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return currentList;
    const seen = new Set(currentList.map((s) => s.toLowerCase()));
    const merged = [...currentList];
    for (const p of parts) {
      if (!seen.has(p.toLowerCase())) {
        merged.push(p);
        seen.add(p.toLowerCase());
      }
    }
    if (merged.length !== currentList.length) onChange(merged);
    return merged;
  }

  function add() {
    const next = addMany(v, values);
    setV('');
    return next;
  }

  // Expose the flush operation to the parent so it can auto-add pending
  // text on form submit. Return value is the new array (React state
  // updates are async — the caller reads it synchronously).
  useEffect(() => {
    if (!flushRef) return undefined;
    flushRef.current = () => {
      if (!v.trim()) return values;
      return add();
    };
    return () => { if (flushRef) flushRef.current = null; };
  }, [v, values]);

  const hasText = v.trim().length > 0;

  return (
    <div className="field">
      <label>{label}</label>
      <div className="row" style={{ gap: 8 }}>
        <input
          className="input"
          value={v}
          onChange={(e) => {
            const val = e.target.value;
            // If the user typed a comma, treat it as a separator: flush
            // everything before the comma into chips, keep any trailing
            // fragment as the new input value.
            if (val.includes(',')) {
              const parts = val.split(',');
              const tail = parts.pop();
              addMany(parts.join(','), values);
              setV(tail);
            } else {
              setV(val);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          onBlur={() => { if (hasText) add(); }}
          placeholder={placeholder}
        />
        <button
          type="button"
          className={hasText ? 'btn btn-primary' : 'btn btn-outline'}
          onClick={add}
          disabled={!hasText}
        >
          <Plus size={14} /> Add
        </button>
      </div>
      {helper && (
        <div className="muted text-xs" style={{ marginTop: 6 }}>{helper}</div>
      )}
      <div className="tag-stack" style={{ marginTop: 8 }}>
        {values.map((t) => (
          <span key={t} className="badge brand">
            {t}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== t))}
              style={{ background: 'transparent', border: 0, marginLeft: 6, cursor: 'pointer', color: 'inherit' }}
              aria-label={`Remove ${t}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function Profile() {
  const { user, refresh } = useAuth();
  // Medical fields are only relevant for end users. Clinic / staff / admin /
  // super-admin accounts see Account-only.
  const showMedical = user?.role === ROLES.USER;
  const isClinic = user?.role === ROLES.VENDOR;

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    medicalProfile: {
      gender: '', dateOfBirth: '', icOrPassport: '',
      allergies: [], existingConditions: [], currentMedications: [],
    }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  // Flush handles from each ChipInput — called on submit so unfinished
  // typed text (e.g. "Peanut" the user forgot to tap Add on) gets
  // converted into a chip before the payload is built.
  const allergiesFlush   = useRef(null);
  const conditionsFlush  = useRef(null);
  const medicationsFlush = useRef(null);

  useEffect(() => {
    usersApi.getProfile()
      .then((u) => {
        const m = u.medicalProfile || {};
        setForm({
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          medicalProfile: {
            gender: m.gender || '',
            dateOfBirth: m.dateOfBirth ? m.dateOfBirth.slice(0, 10) : '',
            icOrPassport: m.icOrPassport || '',
            allergies: m.allergies || [],
            existingConditions: m.existingConditions || [],
            currentMedications: m.currentMedications || [],
          }
        });
      })
      .catch((e) => setErr(errorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  function setM(k, v) {
    setForm((s) => ({ ...s, medicalProfile: { ...s.medicalProfile, [k]: v } }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      const m = form.medicalProfile;
      // Flush pending typed-but-not-added text in the tag inputs BEFORE
      // building the payload. Each flush returns the up-to-date array so
      // we don't have to wait for React's async state update.
      const allergies         = allergiesFlush.current?.()   ?? m.allergies;
      const existingConditions = conditionsFlush.current?.() ?? m.existingConditions;
      const currentMedications = medicationsFlush.current?.() ?? m.currentMedications;

      const payload = {
        name: form.name,
        email: form.email ? form.email.trim().toLowerCase() : undefined,
        phone: form.phone,
      };
      if (showMedical) {
        payload.medicalProfile = {
          gender: m.gender || undefined,
          dateOfBirth: m.dateOfBirth || undefined,
          icOrPassport: m.icOrPassport || undefined,
          allergies,
          existingConditions,
          currentMedications,
        };
      }
      await usersApi.updateProfile(payload);
      await refresh();
      toast.success('Profile updated');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <>
      <PageHeader
        title="Profile"
        subtitle={showMedical
          ? 'Keep your details current. Clinics use this to interpret your results accurately.'
          : 'Keep your account details current.'}
      />

      <form onSubmit={onSubmit}>
        <div className={showMedical ? 'grid grid-2' : isClinic ? 'grid grid-2' : ''}>
          <div className="card">
            <div className="card-title"><h3>Account</h3></div>
            <FormInput label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <FormInput label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60 12-345 6789" />
            <FormInput
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
            />
          </div>

          {isClinic && (
            <div className="card">
              <div className="card-title">
                <h3>
                  <Building2 size={18} style={{ verticalAlign: 'middle', color: 'var(--brand-700)' }} />{' '}
                  Clinic
                </h3>
              </div>

              <div className="field">
                <label>Clinic name</label>
                <div className="bold" style={{ fontSize: 16 }}>
                  {user?.clinicProfile?.clinicName || <span className="muted">Not set yet</span>}
                </div>
              </div>

              <div className="field">
                <label>Approval status</label>
                <div>
                  {(() => {
                    const s = user?.clinicProfile?.approvalStatus || 'PENDING';
                    const tone = s === 'APPROVED' ? 'success'
                      : s === 'PENDING' ? 'warning'
                      : 'danger';
                    return <span className={`badge ${tone}`}>{s.toLowerCase()}</span>;
                  })()}
                </div>
              </div>

              <Link to="/clinic-profile" className="btn btn-outline" style={{ marginTop: 'var(--s-2)' }}>
                Manage clinic details (MMC / APC, address, doctor) <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {showMedical && (
            <div className="card">
              <div className="card-title">
                <h3><HeartPulse size={18} style={{ verticalAlign: 'middle', color: 'var(--brand-700)' }} /> Health basics</h3>
              </div>
              <div className="grid grid-2">
                <SelectInput
                  label="Gender"
                  value={form.medicalProfile.gender}
                  onChange={(e) => setM('gender', e.target.value)}
                  placeholder="Select…"
                  options={GENDERS}
                />
                <FormInput label="Date of birth" type="date" value={form.medicalProfile.dateOfBirth} onChange={(e) => setM('dateOfBirth', e.target.value)} />
              </div>
              <FormInput label="IC / Passport" value={form.medicalProfile.icOrPassport} onChange={(e) => setM('icOrPassport', e.target.value)} placeholder="e.g. 950101-14-1234" />
            </div>
          )}
        </div>

        {showMedical && (
          <div className="card" style={{ marginTop: 'var(--s-4)' }}>
            <div className="card-title"><h3>Medical history</h3></div>
            <div className="grid grid-3">
              <ChipInput
                label="Allergies"
                values={form.medicalProfile.allergies}
                onChange={(v) => setM('allergies', v)}
                placeholder="Type allergy and press Enter"
                helper="Press Enter or tap + Add to attach item"
                flushRef={allergiesFlush}
              />
              <ChipInput
                label="Existing conditions"
                values={form.medicalProfile.existingConditions}
                onChange={(v) => setM('existingConditions', v)}
                placeholder="Type condition and press Enter"
                helper="Press Enter or tap + Add to attach item"
                flushRef={conditionsFlush}
              />
              <ChipInput
                label="Current medications"
                values={form.medicalProfile.currentMedications}
                onChange={(v) => setM('currentMedications', v)}
                placeholder="Type medication and press Enter"
                helper="Press Enter or tap + Add to attach item"
                flushRef={medicationsFlush}
              />
            </div>
          </div>
        )}

        {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}

        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? <span className="spinner" /> : <Save size={16} />}
            Save profile
          </button>
        </div>
      </form>
    </>
  );
}

export default Profile;
